import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-8b16c9d6"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  id: "graphTitle",
  class: "graph-title",
  style: {"display":"flex","align-items":"center","justify-content":"space-between","margin-bottom":"15px"},
  title: "Descargar CSV"
}
const _hoisted_2 = { style: {"font-size":"21px","margin":"0"} }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", {
  class: "fa fa-download",
  style: {"margin-right":"5px"}
}, null, -1))
const _hoisted_4 = /*#__PURE__*/_createTextVNode(" Descargar CSV ")

import { onMounted, ref, watch, computed } from "vue";
import { ReportFilter } from "@/types/report.type";
import { Estimation, ValidationFilter_2 } from "@/types/estimation.type";
import { getPaginatedEstimations } from "@/service/estimation.service";
import { Filter } from "@/types/filter.type";
import { getParameter } from "@/service/parameter.service";
import { ElTable, ElTableColumn, ElPagination } from "element-plus";
import { rageDateFormat } from "@/utils/date.utils";
import { i18n } from "@/plugins/i18n.plugin";


export default /*#__PURE__*/_defineComponent({
  props: {
    reportFilter: null
  },
  setup(__props: any) {

const props = __props as { reportFilter: ReportFilter }



const tableData = ref<
  {
    date: number | null;
    values: { [parameterId: string]: number | null };
  }[]
>([]);

const uniqueParameters = ref<string[]>([]);
const parameterNames = ref<{ [parameterId: string]: { name: string; unit: string } }>({});
const graphTitle = ref("");
const docTitle = ref("");

const currentPage = ref(1);
const pageSize = ref(10);

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return tableData.value.slice(start, start + pageSize.value);
});

const changePaginationSize = (size: number) => {
  pageSize.value = size;
  currentPage.value = 1;
};

async function downloadCsv() {
  if (tableData.value.length === 0) {
    // eslint-disable-next-line no-console
    console.warn("No hay datos disponibles para descargar.");
    return;
  }

  const delimiter = ";";

  const parameterCodes: { [paramId: string]: string } = {};

  for (const paramId of uniqueParameters.value) {
    const filterParam: Filter = {
      fields: {
        "parameter.id": {
          operator: "equal",
          value: Number(paramId),
        },
      },
    };

    const parameterResponse = await getParameter(undefined, undefined, filterParam);
    parameterCodes[paramId] = parameterResponse?.data?.[0]?.code || `ID_${paramId}`;
  }

  let csvContent = "Fecha y Hora" + delimiter;
  csvContent +=
    uniqueParameters.value.map((paramId) => parameterCodes[paramId]).join(delimiter) + "\n";

  tableData.value.forEach((row) => {
    const formattedDate = `"${formatDate(row.date)}"`;

    const values = uniqueParameters.value.map((paramId) => {
      const value = row.values[paramId] ?? "-";
      return `"${value}"`;
    });

    csvContent += `${formattedDate}${delimiter}${values.join(delimiter)}\n`;
  });

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;

  link.download = `${docTitle.value}.csv`;

  link.click();

  URL.revokeObjectURL(url);
}

function formatDate(timestamp: number | null): string {
  if (!timestamp) return "-";
  const date = new Date(timestamp);
  return date.toLocaleString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getParameterLabel(parameterId: string): string {
  const parameter = parameterNames.value[parameterId];
  return parameter ? `${parameter.name} (${parameter.unit || "-"})` : parameterId;
}

async function updateGraphTitle(parameterIds: number[]) {
  const parameterNamesList: string[] = [];

  for (const paramId of parameterIds) {
    const filterParam: Filter = {
      fields: {
        "parameter.id": {
          operator: "equal",
          value: paramId,
        },
      },
    };
    const parameterResponse = await getParameter(undefined, undefined, filterParam);
    const parameterName = parameterResponse?.data[0]?.name || "-";
    parameterNamesList.push(parameterName);
  }

  if (parameterNamesList.length === 1) {
    graphTitle.value = `${parameterNamesList[0]}: ${rageDateFormat(props.reportFilter.date)}`;
    docTitle.value = `${parameterNamesList[0]}`;
  } else {
    graphTitle.value = `${parameterNamesList.join(" y ")}: ${rageDateFormat(
      props.reportFilter.date
    )}`;
    docTitle.value = `${parameterNamesList.join(" y ")}`;
  }
}

async function createTable() {
  if (props.reportFilter.date[0] === null || props.reportFilter.parameterIds[0] === null) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tempData: { [key: string]: any } = {};

  uniqueParameters.value = [];

  for (const paramId of props.reportFilter.parameterIds) {
    const validationFilter = {
      samplingPointId: props.reportFilter.samplingPointIds,
      parameterId: [paramId],
      date: [props.reportFilter.date[0], props.reportFilter.date[1]],
    } as ValidationFilter_2;

    const estimations = await getPaginatedEstimations(validationFilter);

    if (estimations.length > 0) {
      const filterParam: Filter = {
        fields: {
          "parameter.id": {
            operator: "equal",
            value: paramId,
          },
        },
      };

      const parameterResponse = await getParameter(undefined, undefined, filterParam);
      const parameterName = parameterResponse?.data?.[0]?.name || `ID ${paramId}`;
      const parameterUnit = parameterResponse?.data?.[0]?.unit || "";

      parameterNames.value[paramId] = { name: parameterName, unit: parameterUnit };

      estimations.forEach((estimation: Estimation) => {
        const dateKey = estimation.dateTime
          ? new Date(estimation.dateTime.toString()).getTime()
          : null;
        if (dateKey !== null) {
          if (!tempData[dateKey]) {
            tempData[dateKey] = { date: dateKey, values: {} };
          }

          const formattedValue =
            parameterNames.value[estimation.parameterId]?.name === "Matriz de Agua"
              ? estimation.value !== undefined
                ? Math.trunc(estimation.value)
                : "-"
              : estimation.value !== undefined
              ? i18n.global.n(estimation.value, "default")
              : // estimation.value
                "-";

          tempData[dateKey].values[estimation.parameterId] = formattedValue;
        }
      });

      if (!uniqueParameters.value.includes(paramId.toString())) {
        uniqueParameters.value.push(paramId.toString());
      }
    }
  }

  tableData.value = Object.values(tempData).sort((a, b) => a.date - b.date);

  if (tableData.value.length === 0) {
    graphTitle.value = "No hay datos para los parámetros seleccionados";
  }
}

onMounted(async () => {
  await updateGraphTitle(props.reportFilter.parameterIds);
  await createTable();
});

watch(
  () => props.reportFilter,
  async (newFilter) => {
    const hasValidParams = newFilter.parameterIds.some((id) => id > 0);
    const hasValidDate = newFilter.date[0] !== null && newFilter.date[1] !== null;

    if (!hasValidParams || !hasValidDate) {
      tableData.value = [];
      graphTitle.value = "No hay datos para los parámetros seleccionados";
      return;
    }

    await updateGraphTitle(newFilter.parameterIds.filter((id) => id > 0));
    await createTable();
  },
  { deep: true, immediate: false }
);

function handlePageChange(page: number) {
  currentPage.value = page;
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h3", _hoisted_2, _toDisplayString(graphTitle.value), 1),
      _createVNode(_component_el_button, {
        type: "primary",
        onClick: downloadCsv
      }, {
        default: _withCtx(() => [
          _hoisted_3,
          _hoisted_4
        ]),
        _: 1
      })
    ]),
    _createVNode(_unref(ElTable), {
      data: _unref(paginatedData),
      stripe: "",
      border: "",
      "header-sticky": "",
      style: {"width":"100%"},
      "max-height": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(ElTableColumn), {
          prop: "date",
          label: "Fecha y Hora",
          width: "200"
        }, {
          default: _withCtx((scope) => [
            _createTextVNode(_toDisplayString(_unref(rageDateFormat)([new Date(scope.row.date), null])), 1)
          ]),
          _: 1
        }),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uniqueParameters.value, (parameterId) => {
          return (_openBlock(), _createBlock(_unref(ElTableColumn), {
            key: parameterId,
            prop: 'values.' + parameterId,
            label: getParameterLabel(parameterId)
          }, {
            default: _withCtx((scope) => [
              _createTextVNode(_toDisplayString(scope.row.values[parameterId] || "-"), 1)
            ]),
            _: 2
          }, 1032, ["prop", "label"]))
        }), 128))
      ]),
      _: 1
    }, 8, ["data"]),
    _createVNode(_unref(ElPagination), {
      layout: "total, sizes, prev, pager, next, jumper",
      "page-size": pageSize.value,
      total: tableData.value.length,
      "current-page": currentPage.value,
      "onUpdate:current-page": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
      onCurrentChange: handlePageChange,
      "page-sizes": [10, 20, 50, 100],
      onSizeChange: changePaginationSize,
      style: {"margin-top":"20px","text-align":"center"}
    }, null, 8, ["page-size", "total", "current-page"])
  ], 64))
}
}

})